import React, { useEffect, useMemo, useState } from "react";
import { Eye, PenSquare, Plus, RefreshCw, Search, Send, User, X, XCircle } from "lucide-react";
import { Program, programsApi, trainerApi, Trainer, TrainerStatus, TrainerTag } from "../api";
import { useAuth } from "../hooks/useAuth";
import { api } from "../api";
import { TRAINER_TAG_BADGE_STYLES, TRAINER_TAG_OPTIONS } from "../constants/trainers";
import ModalPortal from "./ModalPortal";

interface MobilisationPartner {
  mp_id: number;
  name: string;
}

interface TrainerFormState {
  full_name: string;
  email: string;
  phone: string;
  program_id: number | "";
  trainer_tag: TrainerTag;
}

const statusStyles: Record<TrainerStatus, { label: string; badge: string }> = {
  Invited: { label: "Invited", badge: "bg-sky-100 text-sky-700" },
  InProgress: { label: "In Progress", badge: "bg-amber-100 text-amber-700" },
  Completed: { label: "Completed", badge: "bg-emerald-100 text-emerald-700" },
};

const resolveStatus = (status: TrainerStatus) => statusStyles[status];

const initialFormState: TrainerFormState = {
  full_name: "",
  email: "",
  phone: "",
  program_id: "",
  trainer_tag: "Trainer",
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const boolText = (value?: boolean | null) =>
  value === undefined || value === null ? "-" : value ? "Yes" : "No";

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <tr>
    <td className="px-4 py-2 text-sm font-medium text-gray-600">{label}</td>
    <td className="px-4 py-2 text-sm text-gray-800">{value ?? "-"}</td>
  </tr>
);

const TrainerManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isSystemAdmin = currentUser?.role === "SYSTEM_ADMIN";
  const isMpAdmin = currentUser?.role === "MOBILISATION_PARTNER_ADMIN";
  const isTrainer = currentUser?.role === "TRAINER";

  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mps, setMps] = useState<MobilisationPartner[]>([]);
  const [mpFilter, setMpFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<TrainerStatus | "ALL">("ALL");
  const [trainerTagFilter, setTrainerTagFilter] = useState<TrainerTag | "ALL">("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<TrainerFormState>(initialFormState);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editingTrainer, setEditingTrainer] = useState<Trainer | null>(null);
  const [viewingTrainer, setViewingTrainer] = useState<Trainer | null>(null);
  const [resending, setResending] = useState<number | null>(null);

  const resolveDefaultProgramId = (items: Program[]) =>
    items.find((program) => program.program_id === 2)?.program_id ?? items[0]?.program_id ?? "";

  const loadPrograms = async () => {
    try {
      const response = await programsApi.getPrograms({ page: 1, limit: 500 });
      const items = response.data ?? [];
      setPrograms(items);
      if (!editingTrainer && !formData.program_id) {
        const defaultProgramId = resolveDefaultProgramId(items);
        setFormData((prev) => ({ ...prev, program_id: defaultProgramId }));
      }
    } catch (err: any) {
      setError(err.response?.data?.message ?? "Failed to load active programs.");
    }
  };

  const fetchPartners = async () => {
    if (!isSystemAdmin) return;
    try {
      const response = await api.get("/partners");
      setMps(response.data?.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message ?? "Failed to load mobilisation partners.");
    }
  };

  const loadTrainers = async () => {
    try {
      setLoading(true);
      setError(null);
      if (isTrainer) {
        const response = await trainerApi.getSelfProfile();
        const record = response.data ? [response.data] : [];
        setTrainers(record);
      } else {
        const response = await trainerApi.getTrainers({
          status: statusFilter === "ALL" ? undefined : statusFilter,
          mp_id: isSystemAdmin ? mpFilter : currentUser?.mp_id,
          trainer_tag: trainerTagFilter === "ALL" ? undefined : trainerTagFilter,
          page: 1,
          limit: 200,
        });
        setTrainers(response.data ?? []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message ?? "Failed to load trainers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSystemAdmin) fetchPartners();
  }, [isSystemAdmin]);

  useEffect(() => {
    // Program list is only needed for MP admin invite/edit actions.
    // Center admin / project manager can view trainers without this call.
    if (isMpAdmin) {
      loadPrograms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMpAdmin]);

  useEffect(() => {
    loadTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, mpFilter, trainerTagFilter, isTrainer]);

  const resetForm = () => {
    setFormData(initialFormState);
    setFormErrors({});
    setEditingTrainer(null);
    setShowForm(false);
  };

  const validate = (data: TrainerFormState) => {
    const errors: Record<string, string> = {};
    if (!data.full_name.trim()) errors.full_name = "Full name is required";
    if (!data.email.trim()) errors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(data.email.trim())) errors.email = "Enter a valid email";
    if (!data.phone.trim()) errors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(data.phone.trim())) errors.phone = "Phone must be 10 digits";
    if (!data.trainer_tag) errors.trainer_tag = "Trainer tag is required";
    if (!data.program_id) errors.program_id = "Program is required";
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedData: TrainerFormState = {
      ...formData,
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
    };
    const errors = validate(trimmedData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (!programs.length) {
      setError("No active programs available. Please create an active program first.");
      return;
    }
    const payload = {
      ...trimmedData,
      program_id: Number(trimmedData.program_id),
    };
    try {
      setLoading(true);
      setError(null);
      if (editingTrainer) {
        await trainerApi.updateTrainer(String(editingTrainer.trainer_id), payload);
        setSuccess("Trainer updated successfully.");
      } else {
        await trainerApi.createTrainer(payload);
        setSuccess("Trainer invitation sent successfully.");
      }
      resetForm();
      loadTrainers();
    } catch (err: any) {
      setError(err.response?.data?.message ?? "Failed to save trainer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredTrainers = useMemo(() => {
    if (isTrainer) return trainers;
    if (!searchTerm.trim()) return trainers;
    const term = searchTerm.toLowerCase();
    return trainers.filter((trainer) =>
      [
        trainer.contact.full_name,
        trainer.contact.email,
        trainer.contact.phone,
        trainer.program?.name ?? "",
        trainer.profile?.top_specializations ?? "",
        trainer.trainer_tag,
      ].some((value) => value?.toLowerCase().includes(term))
    );
  }, [searchTerm, trainers, isTrainer]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setTrainerTagFilter("ALL");
    setMpFilter(undefined);
    loadTrainers();
  };

  const programLabel = (trainer?: Trainer | null) => trainer?.program?.name ?? "-";

  const handleResendInvite = async (trainer: Trainer) => {
    setResending(trainer.trainer_id);
    try {
      await trainerApi.resendTrainerInvite(trainer.trainer_id);
      setSuccess("Onboarding email resent to trainer.");
      loadTrainers();
    } catch (err: any) {
      setError(
        err.response?.data?.message ??
          "Failed to resend onboarding invite. Please try again."
      );
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span>{error}</span>
          <button className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded relative">
          <span>{success}</span>
          <button className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setSuccess(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isTrainer ? "My Trainer Profile" : "Trainer Management"}
          </h1>
          <p className="text-sm text-gray-600">
            {isTrainer
              ? "View the details Bajaj has on file for your trainer profile."
              : isSystemAdmin
              ? "Monitor trainer invitations and onboarding milestones."
              : "Invite trainers and track onboarding progress."}
          </p>
        </div>
        {isMpAdmin && (
          <button
            className="btn-primary flex items-center"
            onClick={() => {
              setShowForm(true);
              setEditingTrainer(null);
              setFormData({
                ...initialFormState,
                program_id: resolveDefaultProgramId(programs),
              });
            }}
          >
            <Plus className="w-4 h-4 mr-2" /> Invite Trainer
          </button>
        )}
      </div>

      {!isTrainer && (
        <div className="bg-white p-5 rounded-xl shadow-sm border space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Filters</p>
              <p className="text-xs text-gray-500">Search or narrow by status, partner, or tag.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="btn-secondary flex items-center"
                onClick={loadTrainers}
                type="button"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </button>
              <button
                className="btn-secondary flex items-center"
                onClick={handleClearFilters}
                type="button"
              >
                <XCircle className="w-4 h-4 mr-2" /> Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search trainers"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="input pl-10 w-full"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as TrainerStatus | "ALL")}
              className="input w-full"
            >
              <option value="ALL">All statuses</option>
              {Object.entries(statusStyles).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.label}
                </option>
              ))}
            </select>
            {isSystemAdmin && (
              <select
                value={mpFilter ?? ""}
                onChange={(event) =>
                  setMpFilter(event.target.value ? Number(event.target.value) : undefined)
                }
                className="input w-full"
              >
                <option value="">All partners</option>
                {mps.map((partner) => (
                  <option key={partner.mp_id} value={partner.mp_id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={trainerTagFilter}
              onChange={(event) => setTrainerTagFilter(event.target.value as TrainerTag | "ALL")}
              className="input w-full"
            >
              <option value="ALL">All tags</option>
              {TRAINER_TAG_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trainer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Onboarding
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTrainers.map((trainer) => (
                    <tr key={trainer.trainer_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                            <User className="w-5 h-5" />
                          </div>
                          <div className="ml-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {trainer.contact.full_name}
                              </span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${TRAINER_TAG_BADGE_STYLES[trainer.trainer_tag]}`}
                              >
                                {trainer.trainer_tag}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">#{trainer.trainer_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>{trainer.contact.email}</div>
                        <div className="text-gray-500">{trainer.contact.phone}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {programLabel(trainer)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {trainer.status === "Invited" && "Invitation sent"}
                        {trainer.status === "InProgress" && (
                          <span>Started {formatDate(trainer.onboarding_started_at)}</span>
                        )}
                        {trainer.status === "Completed" && (
                          <span>Completed {formatDate(trainer.onboarding_completed_at)}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const meta = resolveStatus(trainer.status);
                          return (
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${meta.badge}`}
                            >
                              {meta.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={() => setViewingTrainer(trainer)}
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isMpAdmin && !isTrainer && trainer.status !== "Completed" && (
                          <>
                            <button
                              type="button"
                              className="text-blue-600 hover:text-blue-900"
                              onClick={() => {
                                setEditingTrainer(trainer);
                                setFormData({
                                  full_name: trainer.contact.full_name,
                                  email: trainer.contact.email,
                                  phone: trainer.contact.phone,
                                  program_id: trainer.program_id ?? resolveDefaultProgramId(programs),
                                  trainer_tag: trainer.trainer_tag,
                                });
                                setShowForm(true);
                              }}
                              title="Edit contact"
                            >
                              <PenSquare className="w-4 h-4" />
                            </button>
                            {trainer.status === "Invited" && (
                              <button
                                type="button"
                                className="text-sky-600 hover:text-sky-900"
                                onClick={() => handleResendInvite(trainer)}
                                title="Resend onboarding email"
                                disabled={resending === trainer.trainer_id}
                              >
                                {resending === trainer.trainer_id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredTrainers.length === 0 && !loading && (
              <div className="text-center py-10 text-sm text-gray-500">
                No trainers match the current filters.
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTrainer ? "Edit Trainer" : "Invite Trainer"}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full name</label>
                <input
                  className="input mt-1"
                  value={formData.full_name}
                  onChange={(event) => {
                    setFormData((prev) => ({ ...prev, full_name: event.target.value }));
                    setFormErrors((prev) => ({ ...prev, full_name: "" }));
                    setError(null);
                  }}
                  placeholder="Trainer full name"
                />
                {formErrors.full_name && (
                  <p className="text-xs text-red-600 mt-1">{formErrors.full_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  className="input mt-1"
                  type="email"
                  value={formData.email}
                  onChange={(event) => {
                    setFormData((prev) => ({ ...prev, email: event.target.value }));
                    setFormErrors((prev) => ({ ...prev, email: "" }));
                    setError(null);
                  }}
                  placeholder="trainer@example.com"
                />
                {formErrors.email && (
                  <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  className="input mt-1"
                  value={formData.phone}
                  onChange={(event) => {
                    setFormData((prev) => ({ ...prev, phone: event.target.value }));
                    setFormErrors((prev) => ({ ...prev, phone: "" }));
                    setError(null);
                  }}
                  placeholder="10 digit phone number"
                />
                {formErrors.phone && (
                  <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Trainer tag</label>
                <select
                  className="input mt-1"
                  value={formData.trainer_tag}
                  onChange={(event) => {
                    setFormData((prev) => ({
                      ...prev,
                      trainer_tag: event.target.value as TrainerTag,
                    }));
                    setFormErrors((prev) => ({ ...prev, trainer_tag: "" }));
                    setError(null);
                  }}
                >
                  {TRAINER_TAG_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formErrors.trainer_tag && (
                  <p className="text-xs text-red-600 mt-1">{formErrors.trainer_tag}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Program</label>
                <select
                  className="input mt-1"
                  value={formData.program_id}
                  onChange={(event) => {
                    const nextProgramId = event.target.value ? Number(event.target.value) : "";
                    setFormData((prev) => ({
                      ...prev,
                      program_id: nextProgramId,
                    }));
                    setFormErrors((prev) => ({ ...prev, program_id: "" }));
                    setError(null);
                  }}
                  disabled={programs.length === 0}
                >
                  {programs.length === 0 ? (
                    <option value="">No active programs available</option>
                  ) : (
                    programs.map((program) => (
                    <option key={program.program_id} value={program.program_id}>
                      {program.name}
                    </option>
                    ))
                  )}
                </select>
                {formErrors.program_id && (
                  <p className="text-xs text-red-600 mt-1">{formErrors.program_id}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTrainer ? "Save changes" : "Send invite"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </ModalPortal>
      )}

      {viewingTrainer && (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {viewingTrainer.contact.full_name}
                  </h2>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TRAINER_TAG_BADGE_STYLES[viewingTrainer.trainer_tag]}`}
                  >
                    {viewingTrainer.trainer_tag}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Trainer #{viewingTrainer.trainer_id}</p>
              </div>
              <button onClick={() => setViewingTrainer(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <tbody>
                <DetailRow label="Email" value={viewingTrainer.contact.email} />
                <DetailRow label="Phone" value={viewingTrainer.contact.phone} />
                <DetailRow label="Program" value={programLabel(viewingTrainer)} />
                <DetailRow label="Status" value={resolveStatus(viewingTrainer.status).label} />
                <DetailRow label="Onboarding started" value={formatDate(viewingTrainer.onboarding_started_at)} />
                <DetailRow label="Onboarding completed" value={formatDate(viewingTrainer.onboarding_completed_at)} />
                <DetailRow label="Mobilisation partner" value={viewingTrainer.mp_name ?? "-"} />
              </tbody>
            </table>

            {viewingTrainer.profile ? (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">Profile details</h3>
                <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                  <tbody>
                    <DetailRow label="Trainer tag" value={viewingTrainer.trainer_tag} />
                    <DetailRow label="Gender" value={viewingTrainer.profile.gender ?? "-"} />
                    <DetailRow label="Date of birth" value={formatDate(viewingTrainer.profile.date_of_birth)} />
                    <DetailRow label="Home town" value={viewingTrainer.profile.home_town ?? "-"} />
                    <DetailRow label="State" value={viewingTrainer.profile.state ?? "-"} />
                    <DetailRow label="City" value={viewingTrainer.profile.city ?? "-"} />
                    <DetailRow label="Address" value={viewingTrainer.profile.address ?? "-"} />
                    <DetailRow label="PIN" value={viewingTrainer.profile.pin_code ?? "-"} />
                    <DetailRow label="WhatsApp" value={viewingTrainer.profile.whatsapp_number ?? "-"} />
                    <DetailRow label="Native language" value={viewingTrainer.profile.native_language ?? "-"} />
                    <DetailRow label="English proficiency" value={viewingTrainer.profile.english_proficiency ?? "-"} />
                    <DetailRow label="Hindi proficiency" value={viewingTrainer.profile.hindi_proficiency ?? "-"} />
                    <DetailRow label="Occupation" value={viewingTrainer.profile.occupation ?? "-"} />
                    <DetailRow label="Education level" value={viewingTrainer.profile.education_level ?? "-"} />
                    <DetailRow label="Graduation stream" value={viewingTrainer.profile.graduation_stream ?? "-"} />
                    <DetailRow label="Post graduation" value={viewingTrainer.profile.post_graduation_stream ?? "-"} />
                    <DetailRow label="Academic qualifications" value={viewingTrainer.profile.academic_qualifications ?? "-"} />
                    <DetailRow label="Professional qualifications" value={viewingTrainer.profile.professional_qualifications ?? "-"} />
                    <DetailRow label="Certificate courses" value={viewingTrainer.profile.certificate_courses ?? "-"} />
                    <DetailRow label="Facilitation training" value={boolText(viewingTrainer.profile.formal_facilitation_training)} />
                    <DetailRow label="Training institutes" value={viewingTrainer.profile.training_institutes ?? "-"} />
                    <DetailRow label="Employer" value={viewingTrainer.profile.employer_name ?? "-"} />
                    <DetailRow label="Industry" value={viewingTrainer.profile.industry ?? "-"} />
                    <DetailRow label="Experience (years)" value={viewingTrainer.profile.years_of_experience ?? "-"} />
                    <DetailRow label="Designation" value={viewingTrainer.profile.designation ?? "-"} />
                    <DetailRow label="Freelance trainer age" value={viewingTrainer.profile.freelance_trainer_age ?? "-"} />
                    <DetailRow label="Top specialisations" value={viewingTrainer.profile.top_specializations ?? "-"} />
                    <DetailRow label="Trained college students" value={boolText(viewingTrainer.profile.trained_college_students)} />
                    <DetailRow label="Trained entry-level employees" value={boolText(viewingTrainer.profile.trained_entry_level_employees)} />
                    <DetailRow label="Trained corporate leaders" value={boolText(viewingTrainer.profile.trained_corporate_leaders)} />
                    <DetailRow label="Trained NGO beneficiaries" value={boolText(viewingTrainer.profile.trained_ngo_beneficiaries)} />
                    <DetailRow label="Trained educational institutes" value={boolText(viewingTrainer.profile.trained_educational_institutes)} />
                    <DetailRow label="Knowledge updation" value={viewingTrainer.profile.knowledge_updation ?? "-"} />
                    <DetailRow label="States visited" value={viewingTrainer.profile.states_visited_count ?? "-"} />
                    <DetailRow label="Performing arts" value={viewingTrainer.profile.performing_arts_experience ?? "-"} />
                    <DetailRow label="Sports" value={viewingTrainer.profile.sports_experience ?? "-"} />
                    <DetailRow label="Reading preferences" value={viewingTrainer.profile.reading_preferences ?? "-"} />
                    <DetailRow label="Social work" value={viewingTrainer.profile.social_work_details ?? "-"} />
                    <DetailRow label="Own office" value={boolText(viewingTrainer.profile.has_office)} />
                    <DetailRow label="Own car" value={boolText(viewingTrainer.profile.owns_car)} />
                    <DetailRow label="Own laptop" value={boolText(viewingTrainer.profile.owns_laptop)} />
                    <DetailRow label="High-speed broadband" value={boolText(viewingTrainer.profile.has_high_speed_broadband)} />
                    <DetailRow label="Online training experience" value={boolText(viewingTrainer.profile.has_online_training_experience)} />
                    <DetailRow label="Can travel" value={boolText(viewingTrainer.profile.can_travel_for_training)} />
                    <DetailRow label="Availability per month" value={viewingTrainer.profile.availability_per_month ?? "-"} />
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded">
                Trainer has not submitted onboarding details yet.
              </div>
            )}
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default TrainerManagement;

